/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useUniqueId } from '@squiz/dxp-console-support';
import clsx from 'clsx';
import React from 'react';
import { ICON_ASSISTANT, ICON_HELP, ICON_INFO, ICON_REPORT, ICON_WARNING } from '../../Icons';
import { SdsIcon } from '../SdsIcon';
import { AlertProps, alertDefaults } from './Alert.props';
import {
  ALERT_TYPE_ASSISTANT,
  ALERT_TYPE_CUSTOM,
  ALERT_TYPE_HELP,
  ALERT_TYPE_INFO,
  ALERT_TYPE_REPORT,
  ALERT_TYPE_WARNING,
} from './Alert.types';

/**
 * SDS Alert component.
 */
export const Alert = (props: AlertProps): JSX.Element => {
  const {
    buttonList,
    classes,
    customIcon,
    isDark,
    hasBgColor,
    title,
    message,
    type,
    headingLevel = 3,
    ...rest
  } = {
    ...alertDefaults,
    ...props,
  };

  const alertId = useUniqueId();
  const alertClass = clsx(`sds-alert__wrapper`, `sds-alert-type--${type}`, classes, {
    [`sds-alert--dark`]: isDark,
    [`sds-alert--has-bg-color`]: hasBgColor,
  });
  const alertContentId = `sds-alert__content--${alertId}`;

  const getIcon = (): JSX.Element | null => {
    switch (type) {
      case ALERT_TYPE_HELP:
        return ICON_HELP;
      case ALERT_TYPE_INFO:
        return ICON_INFO;
      case ALERT_TYPE_WARNING:
        return ICON_WARNING;
      case ALERT_TYPE_REPORT:
        return ICON_REPORT;
      case ALERT_TYPE_ASSISTANT:
        return ICON_ASSISTANT;
      case ALERT_TYPE_CUSTOM:
        return customIcon || null;
      default:
        return null;
    }
  };

  const getRole = (): string => {
    switch (type) {
      case ALERT_TYPE_REPORT:
      case ALERT_TYPE_WARNING:
        return `alert`;
      default:
        return `status`;
    }
  };

  const alertIcon = getIcon();
  const alertRole = getRole();

  /* eslint-disable jsx-a11y/aria-props */
  return (
    <div aria-describedby={alertContentId} className={alertClass} role={alertRole} {...rest}>
      <div
        className={clsx(`sds-alert__body`, {
          ['sds-alert__body--header']: title,
        })}
      >
        {title ? (
          <div className="sds-alert__body-header">
            {alertIcon && (
              <div className="sds-alert__icon-container">
                <SdsIcon svg={alertIcon} />
              </div>
            )}
            <p aria-level={headingLevel} className="sds-alert__title" role="heading">
              {title}
            </p>
          </div>
        ) : (
          <>
            {alertIcon && (
              <div className="sds-alert__icon-container">
                <SdsIcon svg={alertIcon} />
              </div>
            )}
          </>
        )}
        <div className="sds-alert__content" id={alertContentId}>
          {message}
        </div>
      </div>
      {buttonList && <div className="sds-alert__buttons">{buttonList}</div>}
    </div>
  );
};
